// src/components/common/ErrorBoundary.js
import React, { useState } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import ErrorFeedbackModal from './ErrorFeedbackModal';
import useAuth from '../../hooks/useAuth';

function ErrorFallback({ error, resetErrorBoundary }) {
  const [isModalOpen, setIsModalOpen] = useState(true);
  const { user } = useAuth(); 

  const handleClose = () => {
    setIsModalOpen(false);
    resetErrorBoundary();
    window.location.href = '/'; 
  };

  return (
    <div role="alert">
      {isModalOpen && (
        <ErrorFeedbackModal
          isOpen={isModalOpen}
          onClose={handleClose}
          error={error}
          userId={user ? user.id : 'anonymous'}
        />
      )}
    </div>
  );
}

const ErrorBoundary = ({ children }) => {
  const logError = (error, info) => {
    // Log errors to console in all environments for debugging
    console.error("[ErrorBoundary] Uncaught error:", error);
    console.error("[ErrorBoundary] Component stack:", info?.componentStack);

    // Send error to backend for tracking (fire-and-forget)
    try {
      const API_URL = process.env.REACT_APP_API_URL;
      if (API_URL) {
        fetch(`${API_URL}feedback/submit-error/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: "anonymous",
            errorMessage: (error?.message || "Unknown error").substring(0, 1000),
            errorStack: [
              error?.stack?.substring(0, 2000) || "",
              info?.componentStack
                ? `Component stack:\n${info.componentStack.substring(0, 2000)}`
                : "",
            ]
              .filter(Boolean)
              .join("\n\n"),
            whatHappened: "Automatic crash report from the React error boundary.",
            whatShouldHaveHappened:
              "The page should have rendered without crashing.",
            url: window.location.href,
          }),
        }).catch(() => {}); // Silently fail if reporting fails
      }
    } catch (e) {
      // Don't let error reporting break the error boundary
    }
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={logError}
      onReset={() => {}}
    >
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary;